import { useEffect } from 'react';

import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { useAppDispatch, useAppSelector } from '@core/store';
import { getLocalStorageItem } from '@utils/storage';

import { useGetDynamicComponentsQuery } from '../queries/DynamicComponentsQuery';
import { setComponents } from '../slices/dynamicComponentsSlice';
import {
  BannerData,
  ComponentInstance,
  ComponentType,
  PopupData
} from '../types';
import { getDismissedStorage } from '../utils/dismissalPersistence';
import filterDismissed from '../utils/filterDismissed';
import useFeStaticBanners from './useFeStaticBanners';

const useDynamicComponents = () => {
  const dispatch = useAppDispatch();

  const branchIdFromStore = useAppSelector(
    (state) => state.branch.currentBranchFilter.branchId
  );

  const orgId = getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID) ?? '';
  const branchId =
    branchIdFromStore ||
    getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID) ||
    '';

  const shouldSkip = !orgId || !branchId;

  const { data } = useGetDynamicComponentsQuery(
    { organizationId: orgId, branchId },
    { skip: shouldSkip }
  );

  useFeStaticBanners();

  useEffect(() => {
    if (!data?.components) return;

    const dismissed = getDismissedStorage();

    const globalComponents = data.components.filter((g) => g.page === 'global');

    const bannerGroup = globalComponents.find(
      (g) => g.type === ComponentType.BANNER
    );
    const popupGroup = globalComponents.find(
      (g) => g.type === ComponentType.POPUP
    );

    const rawBanners = (bannerGroup?.instances ??
      []) as ComponentInstance<BannerData>[];
    const rawPopups = (popupGroup?.instances ??
      []) as ComponentInstance<PopupData>[];

    const filteredBanners = filterDismissed(
      rawBanners,
      ComponentType.BANNER,
      dismissed
    ) as ComponentInstance<BannerData>[];

    const popups = filterDismissed(
      rawPopups,
      ComponentType.POPUP,
      dismissed
    ) as ComponentInstance<PopupData>[];

    dispatch(setComponents({ banners: filteredBanners, popups }));
  }, [data, data?.components, dispatch]);
};

export default useDynamicComponents;
